import {getCountryNameForISO2} from "crqlar-react-common/dist/utils/country.utils";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {
    findPrintoutVariable,
    getPrintoutVariableDisplayReference,
    normalizePrintoutDateFormat,
    parsePrintoutVariableReference,
} from "./printoutVariables";
import type {
    PrintoutDateFormat,
    PrintoutVariableCompute,
    PrintoutVariableDefinition,
    PrintoutVariableFormatting,
    PrintoutVariableKey,
} from "./printoutVariables";
import {
    DEFAULT_PRINTOUT_LANGUAGE,
    normalizePrintoutLanguage,
} from "../core/printoutDocument";

type UnknownRecord = Record<string, unknown>;

export interface PrintoutVariableResolveContext extends UnknownRecord {
    ageCategories?: UnknownRecord[];
    agenda?: UnknownRecord;
    agendaEnd?: Date | string;
    agendaItem?: UnknownRecord;
    agendaItems?: UnknownRecord[];
    agendaStart?: Date | string;
    folio?: UnknownRecord;
    folioItem?: UnknownRecord;
    folioItems?: UnknownRecord[];
    language?: string;
    locale?: string;
    now?: Date | string;
    organization?: UnknownRecord;
    profile?: UnknownRecord;
    reservation?: UnknownRecord;
    reservationMealPeriods?: UnknownRecord[];
    reservationProfiles?: UnknownRecord[];
    reservations?: UnknownRecord[];
    resource?: UnknownRecord;
    resourceCategories?: UnknownRecord[];
    resourceCategory?: UnknownRecord;
    resources?: UnknownRecord[];
    restaurant?: UnknownRecord;
    sector?: UnknownRecord;
    sectorResources?: UnknownRecord[];
    service?: UnknownRecord;
    serviceCategories?: UnknownRecord[];
    serviceCategory?: UnknownRecord;
    taxRates?: UnknownRecord[];
    user?: UnknownRecord;
}

export interface ResolvedPrintoutVariable {
    displayValue: string;
    isEmpty: boolean;
    key: string;
    language: string;
    locale: string;
    rawValue: unknown;
    reference: string;
    variable: PrintoutVariableDefinition;
}

interface LanguageMeta {
    explicitLanguage?: string;
    language: string;
    locale: string;
}

type PrintoutVariableComputeResolver = (
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
) => unknown;

const DEFAULT_LANGUAGE = DEFAULT_PRINTOUT_LANGUAGE;
const DEFAULT_LOCALE = "en-GB";
const DEFAULT_CURRENCY = "EUR";
const LIST_SEPARATOR = ", ";
const CONTACT_SEPARATOR = " · ";
const STAFF_RESOURCE_CATEGORY_MATCHES = ["staff", "team", "employee", "personal", "mitarbeiter"];
const FACILITY_RESOURCE_CATEGORY_MATCHES = [
    "facilit",
    "location",
    "room",
    "space",
    "venue",
    "where",
    "anlage",
    "bereich",
    "raum",
];
const RESERVATION_EAT_PERIOD_LABELS: Record<string, Record<string, string>> = {
    de: {
        breakfast: "Frühstück",
        lunch: "Mittagessen",
        dinner: "Abendessen",
        "breakfast-plus-dinner": "Frühstück + Abendessen",
    },
    en: {
        breakfast: "Breakfast",
        lunch: "Lunch",
        dinner: "Dinner",
        "breakfast-plus-dinner": "Breakfast + dinner",
    },
};

const RESERVATION_GUEST_COUNT_LABELS: Record<string, string> = {
    de: "Gäste",
    en: "Guests",
};

const LANGUAGE_LOCALES: Record<string, string> = {
    de: "de-AT",
    en: "en-GB",
    es: "es-ES",
    fr: "fr-FR",
    it: "it-IT",
};

const CURRENCY_NAME_FRAGMENT_REGEX = /(?:^|[\s([{/|,;:–—-])(?:(?:€|\$|£|CHF|EUR|USD|GBP)\s*\d+(?:[.,]\d+)?|\d+(?:[.,]\d+)?\s*(?:€|\$|£|CHF|EUR|USD|GBP))(?=$|[\s)\]}/|,;:–—-])/gi;
const CURRENCY_ONLY_FRAGMENT_REGEX = /(?:^|[\s([{/|,;:–—-])(?:€|\$|£|CHF|EUR|USD|GBP)(?=$|[\s)\]}/|,;:–—-])/gi;

const BOOLEAN_LABELS: Record<string, { false: string; true: string }> = {
    de: {false: "Nein", true: "Ja"},
    en: {false: "No", true: "Yes"},
};

const TAX_INCLUSION_LABELS: Record<string, { exclusive: string; inclusive: string }> = {
    de: {exclusive: "exkl.", inclusive: "inkl."},
    en: {exclusive: "excl.", inclusive: "incl."},
};

const isRecord = (value: unknown): value is UnknownRecord => (
    typeof value === "object" && value !== null && !Array.isArray(value)
);

const isEmptyValue = (value: unknown) => (
    value === undefined
    || value === null
    || value === ""
    || (Array.isArray(value) && value.length === 0)
);

const pathToSegments = (path: string): string[] => (
    path
        .replace(/\[(\d+)]/g, ".$1")
        .split(".")
        .filter(Boolean)
);

export const getValueAtPath = (source: unknown, path: string): unknown => {
    return pathToSegments(path).reduce<unknown>((current, segment) => {
        if (current === undefined || current === null) return undefined;

        if (Array.isArray(current)) {
            const index = Number(segment);
            return Number.isInteger(index) ? current[index] : undefined;
        }

        if (isRecord(current)) {
            return current[segment];
        }

        return undefined;
    }, source);
};

const getStringAtPath = (source: unknown, path: string): string | undefined => {
    const value = getValueAtPath(source, path);
    return typeof value === "string" ? value : undefined;
};

const compact = (values: unknown[]) => (
    values
        .map((value) => typeof value === "string" ? value.trim() : value)
        .filter((value) => !isEmptyValue(value))
);

const unique = <T,>(values: T[]) => Array.from(new Set(values));

const titleCase = (value: string) => (
    value
        .toLowerCase()
        .replace(/(^|_|\s)([a-z])/g, (_, separator, character) => {
            return `${separator === "_" ? " " : separator}${character.toUpperCase()}`;
        })
        .trim()
);

const normalizeLanguage = (value?: unknown) => {
    if (typeof value !== "string") return undefined;

    return normalizePrintoutLanguage(value);
};

export const withPrintoutLanguageContext = (
    context: PrintoutVariableResolveContext,
    language?: string | null,
): PrintoutVariableResolveContext => ({
    ...context,
    language: normalizePrintoutLanguage(language) ?? DEFAULT_LANGUAGE,
});

const getLocaleForLanguage = (
    language: string,
    context: PrintoutVariableResolveContext,
) => {
    const contextLocale = typeof context.locale === "string" ? context.locale : undefined;

    if (contextLocale && normalizeLanguage(contextLocale) === language) {
        return contextLocale;
    }

    return LANGUAGE_LOCALES[language] ?? language ?? DEFAULT_LOCALE;
};

const getLanguageMeta = (
    languageReference: string | undefined,
    context: PrintoutVariableResolveContext,
): LanguageMeta => {
    const explicitLanguage = normalizeLanguage(languageReference);
    const language = explicitLanguage
        ?? normalizeLanguage(context.language)
        ?? normalizeLanguage(context.locale)
        ?? DEFAULT_LANGUAGE;

    return {
        explicitLanguage,
        language,
        locale: getLocaleForLanguage(language, context),
    };
};

const toDate = (value: unknown): Date | null => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    if (typeof value === "string" || typeof value === "number") {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
};

const timeStringToDate = (value: string) => {
    const time = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!time) return null;

    const hours = Number(time[1]);
    const minutes = Number(time[2]);
    const seconds = Number(time[3] ?? 0);

    if (
        !Number.isInteger(hours)
        || !Number.isInteger(minutes)
        || !Number.isInteger(seconds)
        || hours < 0
        || hours > 23
        || minutes < 0
        || minutes > 59
        || seconds < 0
        || seconds > 59
    ) {
        return null;
    }

    return new Date(1970, 0, 1, hours, minutes, seconds);
};

const formatDate = (
    value: unknown,
    meta: LanguageMeta,
    format: PrintoutDateFormat = "numeric",
) => {
    const date = toDate(value);
    if (!date) return typeof value === "string" ? value : "";

    if (format === "numeric") {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");

        return `${day}.${month}.${date.getFullYear()}`;
    }

    return new Intl.DateTimeFormat(meta.locale, {
        ...(format === "weekday" ? {weekday: "long" as const} : {}),
        day: "numeric",
        month: format === "short" ? "short" : "long",
        year: "numeric",
    }).format(date);
};

const getMealPlanDateRange = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const dates = (context.reservationMealPeriods ?? [])
        .map(row => toDate(row.start ?? getValueAtPath(row, "reservation.expectedStartDate")))
        .filter((date): date is Date => Boolean(date))
        .sort((left, right) => left.getTime() - right.getTime());
    if (!dates.length) return "";

    const first = formatDate(dates[0], meta);
    const last = formatDate(dates[dates.length - 1], meta);
    return first === last ? first : `${first} – ${last}`;
};

const formatTime = (
    value: unknown,
    meta: LanguageMeta,
) => {
    const date = typeof value === "string"
        ? timeStringToDate(value) ?? toDate(value)
        : toDate(value);

    if (!date) return typeof value === "string" ? value : "";

    return new Intl.DateTimeFormat(meta.locale, {
        hour: "2-digit",
        hourCycle: "h23",
        minute: "2-digit",
    }).format(date);
};

const getLocalizedText = (value: unknown, meta: LanguageMeta): string | undefined => {
    if (typeof value === "string") return value || undefined;
    if (!isRecord(value)) return undefined;

    const candidates = unique([
        meta.language,
        meta.locale,
        DEFAULT_LANGUAGE,
        "en",
        ...Object.keys(value),
    ].filter((candidate): candidate is string => typeof candidate === "string" && candidate !== ""));

    const key = candidates.find((candidate) => {
        const localizedValue = value[candidate];
        return typeof localizedValue === "string" && localizedValue.trim() !== "";
    });

    return key ? (value[key] as string) : undefined;
};

const formatBoolean = (value: boolean, meta: LanguageMeta) => {
    const labels = BOOLEAN_LABELS[meta.language] ?? BOOLEAN_LABELS.en;
    return value ? labels.true : labels.false;
};

const formatNumber = (value: number, meta: LanguageMeta) => (
    new Intl.NumberFormat(meta.locale).format(value)
);

// Takes percentage points; the locale decides the spacing ("20 %" in German, "20%" in English).
const formatPercent = (value: number, meta: LanguageMeta) => (
    new Intl.NumberFormat(meta.locale, {maximumFractionDigits: 2, style: "percent"}).format(value / 100)
);

const isCurrencyCode = (value: unknown): value is string => (
    typeof value === "string" && /^[A-Za-z]{3}$/.test(value.trim())
);

const getCurrency = (
    value: unknown,
    context: PrintoutVariableResolveContext,
    variable: PrintoutVariableDefinition,
) => {
    const explicitCurrency = variable.attr.currencyPath
        ? getValueAtPath(context, variable.attr.currencyPath)
        : undefined;

    if (isCurrencyCode(explicitCurrency)) return explicitCurrency;

    if (isRecord(value) && isCurrencyCode(value.currency)) return value.currency;

    const folioCurrency = getValueAtPath(context, "folio.currency");
    if (isCurrencyCode(folioCurrency)) return folioCurrency;

    const hotelCurrency = getValueAtPath(context, "organization.currencyCode");
    if (isCurrencyCode(hotelCurrency)) return hotelCurrency;

    return DEFAULT_CURRENCY;
};

const amountToNumber = (value: unknown): number | undefined => {
    if (typeof value === "number") return value;

    if (!isRecord(value)) return undefined;

    if (typeof value.amount === "number") return value.amount;
    if (typeof value.amountCents === "number") return value.amountCents / 100;

    // Never substitute net fields when gross was requested.
    return undefined;
};

const formatCurrency = (
    value: unknown,
    context: PrintoutVariableResolveContext,
    variable: PrintoutVariableDefinition,
    meta: LanguageMeta,
) => {
    const amount = amountToNumber(value);
    if (amount === undefined) return "";

    return new Intl.NumberFormat(meta.locale, {
        currency: getCurrency(value, context, variable),
        style: "currency",
    }).format(amount);
};

const formatPlainValue = (
    value: unknown,
    meta: LanguageMeta,
): string => {
    if (isEmptyValue(value)) return "";

    if (Array.isArray(value)) {
        return compact(value)
            .map((item) => formatPlainValue(item, meta))
            .filter(Boolean)
            .join(LIST_SEPARATOR);
    }

    if (value instanceof Date) return formatDate(value, meta);

    const localizedText = getLocalizedText(value, meta);
    if (localizedText) return localizedText;

    if (typeof value === "boolean") return formatBoolean(value, meta);
    if (typeof value === "number") return formatNumber(value, meta);
    if (typeof value === "string") return value;

    return "";
};

const getAddressLine = (address: unknown) => {
    if (!isRecord(address)) return "";

    return compact([
        address.addressLine1,
        [address.postalCode, address.cityName].filter(Boolean).join(" "),
        address.stateProv,
        address.countryName,
    ]).join(LIST_SEPARATOR);
};

const getResourcesForReservation = (context: PrintoutVariableResolveContext) => {
    const resourceIds = getValueAtPath(context, "reservation.resourceIds");
    const idSet = new Set(Array.isArray(resourceIds) ? resourceIds.filter((id): id is string => typeof id === "string") : []);
    const resourcePool = [
        ...(context.resources ?? []),
        ...(context.sectorResources ?? []),
        ...(context.resource ? [context.resource] : []),
    ];

    // The resource pool may contain sibling bookings; require explicit reservation links.
    if (!idSet.size) return [];

    return resourcePool.filter((resource) => {
        const id = resource.id;
        return typeof id === "string" && idSet.has(id);
    });
};

const uniqueRecordsById = (records: UnknownRecord[]) => {
    const seenIds = new Set<string>();

    return records.filter(record => {
        const id = record.id;
        if (typeof id !== "string") return true;
        if (seenIds.has(id)) return false;

        seenIds.add(id);
        return true;
    });
};

const getSectorType = (context: PrintoutVariableResolveContext) => (
    typeof context.sector?.sectorType === "string" ? context.sector.sectorType : undefined
);

const getRestaurantResource = (context: PrintoutVariableResolveContext) => {
    if (isRecord(context.restaurant)) return context.restaurant;

    const resources = uniqueRecordsById(getResourcesForReservation(context));
    const tableParentIds = new Set(
        resources
            .map(resource => resource.parentId)
            .filter((parentId): parentId is string => typeof parentId === "string" && parentId !== ""),
    );

    return resources.find(resource => typeof resource.id === "string" && tableParentIds.has(resource.id))
        ?? resources.find(resource => !resource.parentId);
};

const getRestaurantTableResources = (context: PrintoutVariableResolveContext) => {
    const sectorResources = uniqueRecordsById(context.sectorResources ?? []);
    if (sectorResources.length) return sectorResources;

    const restaurant = getRestaurantResource(context);
    const resources = uniqueRecordsById(getResourcesForReservation(context));

    return restaurant?.id
        ? resources.filter(resource => resource.id !== restaurant.id)
        : resources.filter(resource => Boolean(resource.parentId));
};

const getReservationDisplayResources = (context: PrintoutVariableResolveContext) => {
    if (getSectorType(context) === "RESTAURANT") {
        return getRestaurantTableResources(context);
    }

    return uniqueRecordsById(getResourcesForReservation(context));
};

const getRecordName = (
    record: UnknownRecord,
    meta: LanguageMeta,
) => (
    getLocalizedText(record.nameLocalized, meta)
    ?? (typeof record.name === "string" ? record.name : undefined)
);

const getResourceById = (context: PrintoutVariableResolveContext, id?: unknown) => {
    if (typeof id !== "string") return undefined;

    return [
        ...(context.resources ?? []),
        ...(context.sectorResources ?? []),
        ...(context.resource ? [context.resource] : []),
    ].find((resource) => resource.id === id);
};

const getReservationRoomResources = (context: PrintoutVariableResolveContext) => {
    const additionalInfo = getValueAtPath(context, "reservation.resourceAdditionalInfo");
    const explicitRoomIds = Array.isArray(additionalInfo)
        ? additionalInfo
            .filter(isRecord)
            .map(info => info.roomID)
            .filter((roomId): roomId is string => typeof roomId === "string" && roomId !== "")
        : [];
    const explicitRooms = explicitRoomIds
        .map(roomId => getResourceById(context, roomId))
        .filter(isRecord);

    if (explicitRooms.length) return uniqueRecordsById(explicitRooms);
    if (getSectorType(context) && getSectorType(context) !== "ACCOMMODATION") return [];
    if (isRecord(context.resource)) return [context.resource];

    return uniqueRecordsById(getResourcesForReservation(context));
};

const getRecordsByIds = (
    records: UnknownRecord[],
    ids: unknown[],
) => {
    const idSet = new Set(ids.filter((id): id is string => typeof id === "string" && id !== ""));
    if (!idSet.size) return records;

    return records.filter((record) => typeof record.id === "string" && idSet.has(record.id));
};

const normalizeMatchText = (value: string) => value.trim().toLowerCase();

const getResourceCategoriesForResource = (
    context: PrintoutVariableResolveContext,
    resource: UnknownRecord,
) => {
    const categoryPool = [
        ...(context.resourceCategories ?? []),
        ...(context.resourceCategory ? [context.resourceCategory] : []),
    ];

    const categoryIds = getRecordIds(resource.categoryIds);

    return categoryIds.length ? getRecordsByIds(categoryPool, categoryIds) : [];
};

const resourceHasCategoryMatch = (
    context: PrintoutVariableResolveContext,
    resource: UnknownRecord,
    meta: LanguageMeta,
    matches: string[],
) => (
    getResourceCategoriesForResource(context, resource)
        .map(category => getRecordName(category, meta))
        .filter((name): name is string => Boolean(name))
        .some((name) => {
            const normalizedName = normalizeMatchText(name);
            return matches.some(match => normalizedName.includes(match));
        })
);

const isStaffResource = (
    context: PrintoutVariableResolveContext,
    resource: UnknownRecord,
    meta: LanguageMeta,
) => resourceHasCategoryMatch(context, resource, meta, STAFF_RESOURCE_CATEGORY_MATCHES);

const isFacilityResource = (
    context: PrintoutVariableResolveContext,
    resource: UnknownRecord,
    meta: LanguageMeta,
) => (
    !isStaffResource(context, resource, meta)
    && resourceHasCategoryMatch(context, resource, meta, FACILITY_RESOURCE_CATEGORY_MATCHES)
);

const getReservationStaffResources = (
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
) => uniqueRecordsById(getResourcesForReservation(context))
    .filter(resource => isStaffResource(context, resource, meta));

const getReservationFacilityResources = (
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
) => {
    const resources = uniqueRecordsById(getResourcesForReservation(context));
    const explicitFacilities = resources.filter(resource => isFacilityResource(context, resource, meta));

    if (explicitFacilities.length) return explicitFacilities;

    return resources.filter(resource => !isStaffResource(context, resource, meta));
};

const getRecordIds = (value: unknown) => (
    Array.isArray(value) ? value.filter((id): id is string => typeof id === "string" && id !== "") : []
);

const getServiceCategories = (context: PrintoutVariableResolveContext) => {
    const categoryPool = [
        ...(context.serviceCategories ?? []),
        ...(context.serviceCategory ? [context.serviceCategory] : []),
    ];

    return getRecordsByIds(categoryPool, getRecordIds(getValueAtPath(context, "service.categoryIds")));
};

const getService = (context: PrintoutVariableResolveContext) => (
    isRecord(context.service) ? context.service : undefined
);

const getPrimaryServiceRate = (context: PrintoutVariableResolveContext) => {
    const rates = getValueAtPath(context, "service.ratesExpanded");
    if (!Array.isArray(rates)) return undefined;

    return rates.find(isRecord);
};

const cleanupRateName = (value?: string) => {
    if (!value) return "";

    return value
        .replace(CURRENCY_NAME_FRAGMENT_REGEX, " ")
        .replace(CURRENCY_ONLY_FRAGMENT_REGEX, " ")
        .replace(/\s*[-–—|,;:]\s*$/g, "")
        .replace(/^\s*[-–—|,;:]\s*/g, "")
        .replace(/\s+/g, " ")
        .trim();
};

const getLocalizedField = (
    record: UnknownRecord,
    localizedField: string,
    plainField: string,
    meta: LanguageMeta,
): string | undefined => {
    const localizedValue = getLocalizedText(record[localizedField], meta);
    if (localizedValue) return localizedValue;

    const plainValue = record[plainField];
    return typeof plainValue === "string" ? plainValue : undefined;
};

const getServiceLocalizedName = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const service = getService(context);
    if (!service) return "";

    /*
        Falls back to the plain name, like rates, resources and categories already do: many
        organizations never fill the localized names, and the guest is better served by the service's
        own name in the wrong language than by the "-" placeholder.
    */
    return getLocalizedField(service, "nameLocalized", "name", meta) ?? "";
};

const getServiceLocalizedDescription = (
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
    preferShort: boolean,
) => {
    const service = getService(context);
    if (!service) return "";

    return getLocalizedText(
        preferShort ? service.shortDescriptionLocalized : service.descriptionLocalized,
        meta,
    ) ?? "";
};

const getServiceRateName = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const rate = getPrimaryServiceRate(context);
    if (!rate) return "";

    return cleanupRateName(getLocalizedField(rate, "nameLocalized", "name", meta));
};

const getServiceRateDescription = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const rate = getPrimaryServiceRate(context);
    if (!rate) return "";

    return getLocalizedField(rate, "descriptionLocalized", "description", meta) ?? "";
};

// Keep the rate's currency with its amount.
const getServicePrice = (context: PrintoutVariableResolveContext) => {
    const rate = getPrimaryServiceRate(context);
    if (!rate || !isRecord(rate.price)) return "";

    const amount = amountToNumber(rate.price);
    if (amount === undefined) return "";

    return {amount, currency: rate.price.currency};
};

const getPriceBeforeTaxAmount = (price: UnknownRecord) => {
    if (typeof price.amountBeforeTax === "number") return price.amountBeforeTax;
    if (typeof price.amountBeforeTaxCents === "number") return price.amountBeforeTaxCents / 100;

    return undefined;
};

const getTaxRateForRate = (
    context: PrintoutVariableResolveContext,
    rate: UnknownRecord,
) => {
    if (isRecord(rate.taxRateSnap)) return rate.taxRateSnap;

    const taxRateId = typeof rate.taxRateId === "string" ? rate.taxRateId : undefined;
    if (!taxRateId) return undefined;

    return context.taxRates?.find(taxRate => taxRate.id === taxRateId);
};

const getServicePriceBeforeTax = (context: PrintoutVariableResolveContext) => {
    const rate = getPrimaryServiceRate(context);
    if (!rate || !isRecord(rate.price)) return "";

    const currency = rate.price.currency;
    const explicitBeforeTax = getPriceBeforeTaxAmount(rate.price);
    if (explicitBeforeTax !== undefined) return {amount: explicitBeforeTax, currency};

    const amount = amountToNumber(rate.price);
    if (amount === undefined) return "";

    const taxRate = getTaxRateForRate(context, rate);
    if (!taxRate || typeof taxRate.rate !== "number") return "";

    const beforeTaxAmount = taxRate.inclusive === true
        ? amount / (1 + taxRate.rate / 100)
        : amount;

    return {amount: beforeTaxAmount, currency};
};

const getFolioItems = (context: PrintoutVariableResolveContext) => (
    (context.folioItems ?? []).filter(isRecord)
);

// A zero-total folio still exists.
const hasFolio = (context: PrintoutVariableResolveContext) => (
    isRecord(context.folio) || getFolioItems(context).length > 0
);

const getFolioCurrency = (context: PrintoutVariableResolveContext) => {
    const folioCurrency = getValueAtPath(context, "folio.currency");
    if (typeof folioCurrency === "string" && folioCurrency) return folioCurrency;

    const totalCurrency = getValueAtPath(context, "folio.total.currency");
    if (typeof totalCurrency === "string" && totalCurrency) return totalCurrency;

    const itemCurrency = getFolioItems(context)
        .map(item => getValueAtPath(item, "totalAmount.currency") ?? getValueAtPath(item, "price.currency"))
        .find((currency): currency is string => typeof currency === "string" && currency !== "");
    if (itemCurrency) return itemCurrency;

    const hotelCurrency = getValueAtPath(context, "organization.currencyCode");
    if (typeof hotelCurrency === "string" && hotelCurrency) return hotelCurrency;

    return DEFAULT_CURRENCY;
};

const amountObject = (
    amount: number,
    context: PrintoutVariableResolveContext,
) => ({
    amount,
    currency: getFolioCurrency(context),
});

const getFolioItemAmount = (
    item: UnknownRecord,
    field: "amount" | "amountBeforeTax",
) => {
    const amount = getValueAtPath(item, `totalAmount.${field}`);
    if (typeof amount === "number") return amount;

    const cents = getValueAtPath(item, `totalAmount.${field}Cents`);
    if (typeof cents === "number") return cents / 100;

    return 0;
};

const getFolioItemsTotal = (
    context: PrintoutVariableResolveContext,
    options: {
        beforeTax?: boolean;
        fixedCharge?: boolean;
    },
) => {
    if (!hasFolio(context)) return undefined;

    const amount = getFolioItems(context)
        .filter(item => Boolean(item.isFixedCharge) === Boolean(options.fixedCharge))
        .filter(item => item.inclusive !== true)
        .reduce((sum, item) => sum + getFolioItemAmount(item, options.beforeTax ? "amountBeforeTax" : "amount"), 0);

    return amountObject(amount, context);
};

const getFolioTotalAmount = (context: PrintoutVariableResolveContext) => (
    amountToNumber(getValueAtPath(context, "folio.total"))
    // Like the backend's total, the sum leaves included lines out.
    ?? getFolioItems(context)
        .filter(item => item.inclusive !== true)
        .reduce((sum, item) => sum + getFolioItemAmount(item, "amount"), 0)
);

const isFolioPaid = (context: PrintoutVariableResolveContext) => {
    const status = getValueAtPath(context, "folio.status");
    return typeof status === "string" && status.toUpperCase() === "PAID";
};

// Nothing is open on these; their providers zero every balance field.
const CLOSED_FOLIO_STATUSES = new Set(["REFUNDED", "RELEASED", "VOID"]);

const isFolioClosed = (context: PrintoutVariableResolveContext) => {
    const status = getValueAtPath(context, "folio.status");
    return typeof status === "string" && CLOSED_FOLIO_STATUSES.has(status.toUpperCase());
};

// folio.amountPaid is whole cents (a Long on the backend), unlike the total's amount record.
const getFolioPaidAmount = (context: PrintoutVariableResolveContext) => {
    const paidCents = getValueAtPath(context, "folio.amountPaid");
    const paid = typeof paidCents === "number" ? paidCents / 100 : 0;
    if (!isFolioPaid(context)) return paid;

    // PAID is settled in full, even with no figure or one still at 0; only a larger payment shows, as a credit.
    const total = getFolioTotalAmount(context);
    return paid !== 0 && paid > total ? paid : total;
};

/*
    The open balance is the total minus what was paid. folio.amountDue is the billed amount and keeps that
    value after payment, so it never stands in for the balance. Negative balances are guest credits; do not
    clamp them.
*/
const getFolioBalance = (context: PrintoutVariableResolveContext) => {
    if (isFolioClosed(context)) return 0;

    // Rounded to cents; `|| 0` drops a negative zero left by float noise, which would print as "-€0.00".
    return Math.round((getFolioTotalAmount(context) - getFolioPaidAmount(context)) * 100) / 100 || 0;
};

const getFolioAmountPaid = (context: PrintoutVariableResolveContext) => (
    hasFolio(context) ? amountObject(getFolioPaidAmount(context), context) : undefined
);

// Due and remaining print the same open balance.
const getFolioAmountDue = (context: PrintoutVariableResolveContext) => (
    hasFolio(context) ? amountObject(getFolioBalance(context), context) : undefined
);

// Bill-to may fall back to the guest only for an existing folio.
const getFolioBillToName = (context: PrintoutVariableResolveContext) => {
    if (!hasFolio(context)) return undefined;

    const billToName = compact([
        getValueAtPath(context, "folio.billTo.firstName"),
        getValueAtPath(context, "folio.billTo.lastName"),
    ]).join(" ");

    if (billToName) return billToName;

    return compact([
        getValueAtPath(context, "profile.firstName"),
        getValueAtPath(context, "profile.lastName"),
    ]).join(" ");
};

const getFolioBillToEmail = (context: PrintoutVariableResolveContext) => {
    if (!hasFolio(context)) return undefined;

    return compact([
        getValueAtPath(context, "folio.billTo.email"),
        getGuestPrimaryEmail(context),
    ])[0];
};

const getFolioBillToPhone = (context: PrintoutVariableResolveContext) => {
    if (!hasFolio(context)) return undefined;

    return compact([
        getValueAtPath(context, "folio.billTo.phone"),
        getGuestPrimaryPhone(context),
    ])[0];
};

const getCurrentFolioItem = (context: PrintoutVariableResolveContext) => (
    isRecord(context.folioItem) ? context.folioItem : undefined
);

// A line without a quantity counts once, as in the folio dialog and the backend's line total.
const getFolioItemQuantity = (context: PrintoutVariableResolveContext) => {
    const item = getCurrentFolioItem(context);
    if (!item) return "";

    return typeof item.quantity === "number" ? item.quantity : 1;
};

const getFolioItemDiscount = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const item = getCurrentFolioItem(context);
    const discounts = item?.discounts;
    if (!Array.isArray(discounts) || !discounts.length) return "";

    return discounts
        .filter(isRecord)
        .map((discount) => {
            if (typeof discount.percentOff === "number") {
                return formatPercent(-discount.percentOff, meta);
            }

            if (typeof discount.amountCentsOff === "number") {
                return formatCurrency(
                    {amount: -(discount.amountCentsOff / 100), currency: getFolioCurrency(context)},
                    context,
                    {
                        key: "folioItemDiscount",
                        chipLabel: "folioItemDiscount",
                        label: "Folio item discount",
                        description: "",
                        scope: "folioItem",
                        scopeLabel: "Folio item",
                        attr: {kind: "currency"},
                    },
                    meta,
                );
            }

            return "";
        })
        .filter(Boolean)
        .join(LIST_SEPARATOR);
};

const getFolioItemCategoryName = (context: PrintoutVariableResolveContext) => {
    const category = getValueAtPath(context, "folioItem.itemCategory");
    return typeof category === "string" ? titleCase(category.replace(/_/g, " ")) : "";
};

// The name is the tax rate's own data; the rate and the inclusion wording are the printout's.
const getFolioItemTaxRate = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const taxRate = getValueAtPath(context, "folioItem.taxRateSnap");
    if (!isRecord(taxRate)) return "";

    const name = typeof taxRate.name === "string" ? taxRate.name : "";
    const rate = typeof taxRate.rate === "number" ? formatPercent(taxRate.rate, meta) : "";
    const labels = TAX_INCLUSION_LABELS[meta.language] ?? TAX_INCLUSION_LABELS.en;
    const inclusion = typeof taxRate.inclusive === "boolean"
        ? (taxRate.inclusive ? labels.inclusive : labels.exclusive)
        : "";

    return compact([name, rate, inclusion]).join(" ");
};

const getFolioItemDuration = (context: PrintoutVariableResolveContext) => {
    const start = toDate(getValueAtPath(context, "folioItem.timeSpanStart"));
    const end = toDate(getValueAtPath(context, "folioItem.timeSpanEnd"));
    if (!start || !end) return "";

    return Math.round((end.getTime() - start.getTime()) / 60000);
};

const getCurrentAgendaItem = (context: PrintoutVariableResolveContext) => (
    isRecord(context.agendaItem) ? context.agendaItem : undefined
);

const formatDateTimeValue = (value: unknown, meta: LanguageMeta) => {
    const date = toDate(value);
    if (!date) return "";

    return `${formatDate(date, meta)} ${formatTime(date, meta)}`;
};

const getAgendaItemName = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const item = getCurrentAgendaItem(context);
    if (!item) return "";

    const service = isRecord(item.service) ? item.service : undefined;
    const serviceName = service ? getRecordName(service, meta) : undefined;
    if (serviceName) return serviceName;

    // Service-less bookings use the sector name.
    const sector = isRecord(item.sector) ? item.sector : undefined;
    return (sector ? getRecordName(sector, meta) : undefined) ?? "";
};

const getAgendaItemSectorName = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const item = getCurrentAgendaItem(context);
    const sector = item && isRecord(item.sector) ? item.sector : undefined;

    return (sector ? getRecordName(sector, meta) : undefined) ?? "";
};

const AGENDA_ITEM_TYPE_LABELS: Record<string, {de: string; en: string}> = {
    OTHER: {de: "Erlebnis", en: "Experience"},
    RESTAURANT: {de: "Kulinarik", en: "Dining"},
    SPA: {de: "Spa", en: "Spa"},
    WEEKLY_PLANNER: {de: "Erlebnis", en: "Experience"},
};

const getAgendaItemTypeLabel = (context: PrintoutVariableResolveContext, meta: LanguageMeta) => {
    const item = getCurrentAgendaItem(context);
    if (!item) return "";

    const sector = isRecord(item.sector) ? item.sector : undefined;
    const sectorType = typeof item.sectorType === "string"
        ? item.sectorType
        : sector && typeof sector.sectorType === "string" ? sector.sectorType : undefined;
    const labels = sectorType ? AGENDA_ITEM_TYPE_LABELS[sectorType] : undefined;

    return labels?.[meta.language as "de" | "en"]
        ?? labels?.en
        ?? (sector ? getRecordName(sector, meta) : undefined)
        ?? "";
};

const getResourceCategories = (context: PrintoutVariableResolveContext) => {
    const categoryPool = [
        ...(context.resourceCategories ?? []),
        ...(context.resourceCategory ? [context.resourceCategory] : []),
    ];
    const categoryIds = getResourcesForReservation(context).flatMap(resource => (
        getRecordIds(resource.categoryIds)
    ));

    return getRecordsByIds(categoryPool, categoryIds);
};

const getGuestCounts = (context: PrintoutVariableResolveContext) => {
    const value = getValueAtPath(context, "reservation.guestCounts");
    return Array.isArray(value) ? value.filter(isRecord) : [];
};

const getAgeCategories = (context: PrintoutVariableResolveContext) => (
    Array.isArray(context.ageCategories) ? context.ageCategories.filter(isRecord) : []
);

const getAgeCategoryName = (
    ageCategory: UnknownRecord | undefined,
    meta: LanguageMeta,
) => {
    if (!ageCategory) return undefined;

    return getLocalizedText(ageCategory.nameLocalized, meta)
        ?? (typeof ageCategory.name === "string" ? ageCategory.name : undefined)
        ?? (typeof ageCategory.type === "string" ? titleCase(ageCategory.type) : undefined);
};

const STANDARD_AGE_CATEGORY_LABELS: Record<string, Record<string, [string, string]>> = {
    de: {
        ADULT: ["Erwachsener", "Erwachsene"],
        CHILD: ["Kind", "Kinder"],
        INFANT: ["Säugling", "Säuglinge"],
        TEENAGER: ["Teenager", "Teenager"],
    },
    en: {
        ADULT: ["Adult", "Adults"],
        CHILD: ["Child", "Children"],
        INFANT: ["Infant", "Infants"],
        TEENAGER: ["Teenager", "Teenagers"],
    },
};

const getAgeCategoryCountLabel = (
    ageCategory: UnknownRecord | undefined,
    count: number,
    meta: LanguageMeta,
) => {
    const storedLabel = getAgeCategoryName(ageCategory, meta);
    const type = typeof ageCategory?.type === "string" ? ageCategory.type : undefined;
    const standard = type ? STANDARD_AGE_CATEGORY_LABELS[meta.language]?.[type] : undefined;
    if (!storedLabel || !standard) return storedLabel;

    const [singular, plural] = standard;

    return storedLabel.toLocaleLowerCase(meta.locale) === singular.toLocaleLowerCase(meta.locale)
        ? count === 1 ? singular : plural
        : storedLabel;
};

const getGuestCountForAgeType = (context: PrintoutVariableResolveContext, ageType: string) => {
    const ageCategoryIds = new Set(
        getAgeCategories(context)
            .filter((category) => category.type === ageType)
            .map((category) => category.id)
            .filter((id): id is string => typeof id === "string"),
    );

    if (!ageCategoryIds.size) return 0;

    return getGuestCounts(context).reduce((sum, guestCount) => {
        return ageCategoryIds.has(guestCount.ageCategoryId as string)
            ? sum + (typeof guestCount.count === "number" ? guestCount.count : 0)
            : sum;
    }, 0);
};

const getTotalGuestCount = (context: PrintoutVariableResolveContext) => (
    getGuestCounts(context).reduce((sum, guestCount) => {
        return sum + (typeof guestCount.count === "number" ? guestCount.count : 0);
    }, 0)
);

const getPluralUnit = (
    count: number,
    meta: LanguageMeta,
    units: {
        de: [string, string];
        en: [string, string];
    },
) => {
    const [singular, plural] = meta.language === "de" ? units.de : units.en;
    return count === 1 ? singular : plural;
};

const formatDurationMinutes = (
    minutes: number,
    meta: LanguageMeta,
) => {
    if (minutes <= 0) return "";

    return `${formatNumber(minutes, meta)} ${getPluralUnit(minutes, meta, {
        de: ["Minute", "Minuten"],
        en: ["minute", "minutes"],
    })}`;
};

const getReservationDuration = (context: PrintoutVariableResolveContext) => {
    const explicitDuration = getValueAtPath(context, "reservation.duration");
    if (!isEmptyValue(explicitDuration)) return explicitDuration;

    const start = toDate(getValueAtPath(context, "reservation.expectedStartDate"));
    const end = toDate(getValueAtPath(context, "reservation.expectedEndDate"));

    if (!start || !end) return "";

    return Math.round((end.getTime() - start.getTime()) / 60000);
};

const getReservationEatPeriod = (
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
) => {
    const value = getValueAtPath(context, "reservation.meta.eatPeriod");
    if (typeof value !== "string") return "";

    const normalized = value.trim().toLowerCase();
    if (!normalized || normalized === "blank") return "";

    const labels = RESERVATION_EAT_PERIOD_LABELS[meta.language] ?? RESERVATION_EAT_PERIOD_LABELS.en;
    return labels[normalized] ?? titleCase(normalized.replace(/[-_]+/g, " "));
};

const FOLIO_STATUS_LABELS: Record<string, Record<string, string>> = {
    de: {
        DRAFT: "Entwurf",
        OPEN: "Offen",
        PAID: "Bezahlt",
        UNCOLLECTABLE: "Uneinbringlich",
        VOID: "Storniert",
        REFUNDED: "Erstattet",
        PREAUTHORIZED: "Vorautorisiert",
        RELEASED: "Freigegeben",
    },
    en: {
        DRAFT: "Draft",
        OPEN: "Open",
        PAID: "Paid",
        UNCOLLECTABLE: "Uncollectable",
        VOID: "Void",
        REFUNDED: "Refunded",
        PREAUTHORIZED: "Preauthorized",
        RELEASED: "Released",
    },
};

/** Localize raw enums; title-case unknown values instead of printing `NO_SHOW`. */
const enumLabelResolver = (
    path: string,
    labels: Record<string, Record<string, string>>,
) => (
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
) => {
    const value = getValueAtPath(context, path);
    if (typeof value !== "string") return "";

    const normalized = value.trim().toUpperCase();
    if (!normalized) return "";

    const languageLabels = labels[meta.language] ?? labels.en;
    return languageLabels[normalized] ?? titleCase(normalized.replace(/[-_]+/g, " "));
};

const RESERVATION_STATUS_LABELS: Record<string, Record<string, string>> = {
    de: {
        PENDING: "Ausstehend",
        CONFIRMED: "Bestätigt",
        ACTIVE: "Aktiv",
        COMPLETED: "Abgeschlossen",
        CANCELLED: "Storniert",
        NO_SHOW: "Nicht erschienen",
        OPTION: "Option",
        UNCOMPLETED: "Nicht abgeschlossen",
    },
    en: {
        PENDING: "Pending",
        CONFIRMED: "Confirmed",
        ACTIVE: "Active",
        COMPLETED: "Completed",
        CANCELLED: "Cancelled",
        NO_SHOW: "No show",
        OPTION: "Option",
        UNCOMPLETED: "Uncompleted",
    },
};

const GUEST_GENDER_LABELS: Record<string, Record<string, string>> = {
    de: {
        MALE: "Männlich",
        FEMALE: "Weiblich",
        NON_BINARY: "Nicht-binär",
        GENDERQUEER: "Genderqueer",
        GENDERFLUID: "Genderfluid",
        AGENDER: "Agender",
        TWO_SPIRIT: "Two-Spirit",
        BIGENDER: "Bigender",
        DEMIBOY: "Demiboy",
        DEMIGIRL: "Demigirl",
        ANDROGYNOUS: "Androgyn",
        NEUTROIS: "Neutrois",
        OTHER: "Andere",
    },
    en: {
        MALE: "Male",
        FEMALE: "Female",
        NON_BINARY: "Non-binary",
        GENDERQUEER: "Genderqueer",
        GENDERFLUID: "Genderfluid",
        AGENDER: "Agender",
        TWO_SPIRIT: "Two-Spirit",
        BIGENDER: "Bigender",
        DEMIBOY: "Demiboy",
        DEMIGIRL: "Demigirl",
        ANDROGYNOUS: "Androgynous",
        NEUTROIS: "Neutrois",
        OTHER: "Other",
    },
};

const FOOD_ALLERGY_LABELS: Record<string, Record<string, string>> = {
    de: {
        MILK: "Milch",
        TREE_NUTS: "Baumnüsse",
        EGGS: "Eier",
        PEANUTS: "Erdnüsse",
        FISH: "Fisch",
        WHEAT: "Weizen",
        SHELLFISH: "Schalentiere",
        SOYBEANS: "Sojabohnen",
        CELERY: "Sellerie",
        MUSTARD: "Senf",
        SESAME: "Sesam",
        SULPHITES: "Sulfite",
        LUPIN: "Lupinen",
        MOLLUSCS: "Weichtiere",
        LACTOSE: "Laktose",
        GARLIC: "Knoblauch",
        PAPRIKA: "Paprika",
        FRUCTOSE: "Fruktose",
        ONION: "Zwiebeln",
        RAW_FRUIT: "Rohes Obst",
        CITRUS: "Zitrusfrüchte",
        TOMATO: "Tomaten",
        MUSHROOMS: "Pilze",
        HISTAMINE: "Histamin",
        FEATHERS: "Federn und Daunen",
        ANIMAL_HAIR: "Tierhaare",
        DUST_MITES: "Hausstaubmilben",
    },
    en: {
        MILK: "Milk",
        TREE_NUTS: "Tree nuts",
        EGGS: "Eggs",
        PEANUTS: "Peanuts",
        FISH: "Fish",
        WHEAT: "Wheat",
        SHELLFISH: "Shellfish",
        SOYBEANS: "Soybeans",
        CELERY: "Celery",
        MUSTARD: "Mustard",
        SESAME: "Sesame",
        SULPHITES: "Sulphites",
        LUPIN: "Lupin",
        MOLLUSCS: "Molluscs",
        LACTOSE: "Lactose",
        GARLIC: "Garlic",
        PAPRIKA: "Paprika",
        FRUCTOSE: "Fructose",
        ONION: "Onion",
        RAW_FRUIT: "Raw fruit",
        CITRUS: "Citrus fruit",
        TOMATO: "Tomatoes",
        MUSHROOMS: "Mushrooms",
        HISTAMINE: "Histamine",
        FEATHERS: "Feathers and down",
        ANIMAL_HAIR: "Animal hair",
        DUST_MITES: "Dust mites",
    },
};

const FOOD_TYPE_LABELS: Record<string, Record<string, string>> = {
    de: {
        VEGETARIAN: "Vegetarisch",
        VEGAN: "Vegan",
        PESCATARIAN: "Pescetarisch",
        PALEO: "Paleo",
        GLUTEN_FREE: "Glutenfrei",
        RAW_FOOD: "Rohkost",
        LOW_CARB: "Low Carb",
        KETO: "Keto",
        HALAL: "Halal",
        KOSHER: "Koscher",
        LACTOSE_FREE: "Laktosefrei",
        NO_ALCOHOL: "Kein Alkohol",
        NO_PORK: "Kein Schweinefleisch",
    },
    en: {
        VEGETARIAN: "Vegetarian",
        VEGAN: "Vegan",
        PESCATARIAN: "Pescatarian",
        PALEO: "Paleo",
        GLUTEN_FREE: "Gluten free",
        RAW_FOOD: "Raw food",
        LOW_CARB: "Low carb",
        KETO: "Keto",
        HALAL: "Halal",
        KOSHER: "Kosher",
        LACTOSE_FREE: "Lactose free",
        NO_ALCOHOL: "No alcohol",
        NO_PORK: "No pork",
    },
};

/** Food preferences only: skip hidden entries, but retain unknown values for kitchen safety. */
const getGuestPreferenceLabels = (
    context: PrintoutVariableResolveContext,
    preferenceType: string,
    labels: Record<string, Record<string, string>>,
    meta: LanguageMeta,
) => {
    const preferences = getValueAtPath(context, "profile.preferences");
    if (!Array.isArray(preferences)) return [];

    const languageLabels = labels[meta.language] ?? labels.en;

    return unique(
        preferences
            .filter(isRecord)
            .filter((preference) => preference.hidden !== true)
            .filter((preference) => (
                typeof preference.preferenceType === "string"
                && preference.preferenceType.trim().toUpperCase() === preferenceType
            ))
            .map((preference) => {
                const value = typeof preference.preferenceValue === "string"
                    ? preference.preferenceValue.trim()
                    : "";
                if (!value) return "";

                return languageLabels[value.toUpperCase()]
                    ?? getLocalizedText(preference.localizations, meta)
                    ?? titleCase(value.replace(/[-_]+/g, " "));
            })
            .filter(Boolean),
    );
};

// Tax requires independently recorded gross and net amounts.
const getFolioTaxTotal = (context: PrintoutVariableResolveContext) => {
    if (!hasFolio(context)) return undefined;

    const total = getValueAtPath(context, "folio.total");
    if (!isRecord(total)) return undefined;

    const grossAmount = amountToNumber(total);
    const netAmount = getPriceBeforeTaxAmount(total);

    if (grossAmount === undefined || netAmount === undefined) return undefined;
    if (grossAmount === netAmount) return undefined;

    return {
        // Gross and net are stored independently; round subtraction to cents.
        amount: Math.round((grossAmount - netAmount) * 100) / 100,
        currency: isCurrencyCode(total.currency) ? total.currency : getFolioCurrency(context),
    };
};

const getAgendaItemTimeRange = (
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
) => {
    const item = getCurrentAgendaItem(context);
    if (!item) return "";

    const start = toDate(item.start);
    const end = toDate(item.end);

    if (!start && !end) return "";
    if (!end) return formatTime(start, meta);
    if (!start) return formatTime(end, meta);

    return `${formatTime(start, meta)}–${formatTime(end, meta)}`;
};

const getAgendaItemLocation = (
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
) => {
    const item = getCurrentAgendaItem(context);
    const resources = item && Array.isArray(item.resources) ? item.resources.filter(isRecord) : [];

    return unique(
        resources
            .map((resource) => getRecordName(resource, meta))
            .filter((name): name is string => Boolean(name)),
    );
};

const getFolioStatus = enumLabelResolver("folio.status", FOLIO_STATUS_LABELS);

const getReservationStatus = enumLabelResolver("reservation.status", RESERVATION_STATUS_LABELS);

const getGuestGender = enumLabelResolver("profile.gender", GUEST_GENDER_LABELS);

/** Resolve ISO2 nationality; preserve unknown codes. */
const getGuestNationality = (context: PrintoutVariableResolveContext) => {
    const nationality = getValueAtPath(context, "profile.nationality");
    if (typeof nationality !== "string" || !nationality.trim()) return "";

    const code = nationality.trim();
    return getCountryNameForISO2(code) || code;
};

const getGuestPrimaryEmail = (context: PrintoutVariableResolveContext) => {
    const emails = getValueAtPath(context, "profile.emails");
    if (!Array.isArray(emails)) return undefined;

    const emailRecords = emails.filter(isRecord);
    const visibleEmailRecords = emailRecords.filter((email) => email.hidden !== true);
    const candidates = visibleEmailRecords.length ? visibleEmailRecords : emailRecords;
    const getEmailValue = (email?: UnknownRecord) => (
        typeof email?.email === "string" ? email.email.trim() || undefined : undefined
    );

    const primaryEmail = candidates.find((email) => email.isPrimary === true && getEmailValue(email));

    // Profiles may have usable addresses without an `isPrimary` flag.
    return primaryEmail ? getEmailValue(primaryEmail) : getEmailValue(candidates[0]);
};

const getGuestPrimaryPhone = (context: PrintoutVariableResolveContext) => {
    const phones = getValueAtPath(context, "profile.phones");
    if (!Array.isArray(phones)) return undefined;

    const phoneRecords = phones.filter(isRecord);
    const visiblePhoneRecords = phoneRecords.filter((phone) => phone.hidden !== true);
    const candidates = visiblePhoneRecords.length ? visiblePhoneRecords : phoneRecords;
    const getPhoneValue = (phone?: UnknownRecord) => {
        if (!phone) return undefined;

        return [
            phone.phoneNumberFull,
            phone.phone,
            phone.number,
        ].find((value): value is string => typeof value === "string" && value.trim().length > 0)?.trim();
    };

    const primaryPhone = candidates.find((phone) => phone.isPrimary === true && getPhoneValue(phone));

    return primaryPhone ? getPhoneValue(primaryPhone) : getPhoneValue(candidates[0]);
};

const getHotelPhone = (context: PrintoutVariableResolveContext) => (
    [
        getValueAtPath(context, "organization.guestContactPhone"),
        getValueAtPath(context, "organization.primaryPhone"),
        getValueAtPath(context, "organization.phone"),
        getValueAtPath(context, "organization.phoneNumber"),
        getValueAtPath(context, "organization.telephone"),
        getValueAtPath(context, "organization.phones[0].phoneNumberFull"),
        getValueAtPath(context, "organization.phones[0].phone"),
    ].find((value): value is string => typeof value === "string" && value.trim().length > 0)?.trim()
);

/** Austrian invoice footer order; stored values do not include labels such as `FN`. */
const HOTEL_LEGAL_LINE_SEGMENTS: readonly {label: string; path: string}[] = [
    {label: "UID", path: "organization.vatId"},
    {label: "FN", path: "organization.commercialRegisterNumber"},
    {label: "IBAN", path: "organization.iban"},
    {label: "BIC", path: "organization.bic"},
];

const getHotelLegalLine = (context: PrintoutVariableResolveContext) => compact(
    HOTEL_LEGAL_LINE_SEGMENTS.map(({label, path}) => {
        const value = getStringAtPath(context, path)?.trim();
        return value ? `${label} ${value}` : undefined;
    }),
).join(CONTACT_SEPARATOR);

const getHotelCountry = (context: PrintoutVariableResolveContext) => {
    const countryName = getValueAtPath(context, "organization.address.countryName");
    if (!isEmptyValue(countryName)) return countryName;

    const countryCode = getValueAtPath(context, "organization.address.countryCode");
    if (typeof countryCode !== "string") return undefined;

    return getCountryNameForISO2(countryCode) || countryCode;
};

const computeResolvers: Record<PrintoutVariableCompute, PrintoutVariableComputeResolver> = {
    agendaItemEnd: (context, meta) => formatDateTimeValue(getCurrentAgendaItem(context)?.end, meta),
    agendaItemLocation: getAgendaItemLocation,
    agendaItemName: getAgendaItemName,
    agendaItemSectorName: getAgendaItemSectorName,
    agendaItemTypeLabel: getAgendaItemTypeLabel,
    agendaItemStart: (context, meta) => formatDateTimeValue(getCurrentAgendaItem(context)?.start, meta),
    agendaItemTimeRange: getAgendaItemTimeRange,
    currentDate: (context) => context.now ?? new Date(),
    currentTime: (context) => context.now ?? new Date(),
    currentUserName: (context) => compact([
        getValueAtPath(context, "user.firstName"),
        getValueAtPath(context, "user.lastName"),
    ]).join(" "),
    folioBillToEmail: getFolioBillToEmail,
    folioBillToName: getFolioBillToName,
    folioBillToPhone: getFolioBillToPhone,
    folioAmountDue: getFolioAmountDue,
    folioAmountPaid: getFolioAmountPaid,
    folioAmountRemaining: getFolioAmountDue,
    folioFixedChargesTotal: (context) => getFolioItemsTotal(context, {fixedCharge: true}),
    folioFixedChargesTotalBeforeTax: (context) => getFolioItemsTotal(context, {beforeTax: true, fixedCharge: true}),
    folioItemCategoryName: getFolioItemCategoryName,
    folioItemDiscount: getFolioItemDiscount,
    folioItemDuration: getFolioItemDuration,
    folioItemQuantity: getFolioItemQuantity,
    folioItemTaxRate: getFolioItemTaxRate,
    folioReservationTotal: (context) => getFolioItemsTotal(context, {fixedCharge: false}),
    folioReservationTotalBeforeTax: (context) => getFolioItemsTotal(context, {beforeTax: true, fixedCharge: false}),
    folioStatus: getFolioStatus,
    folioTaxTotal: getFolioTaxTotal,
    guestAllergies: (context, meta) => getGuestPreferenceLabels(
        context,
        "CRQLAR_FOOD_ALLERGY",
        FOOD_ALLERGY_LABELS,
        meta,
    ),
    guestDietaryPreferences: (context, meta) => getGuestPreferenceLabels(
        context,
        "CRQLAR_FOOD_TYPE",
        FOOD_TYPE_LABELS,
        meta,
    ),
    guestGender: getGuestGender,
    guestListName: (context) => formatProfileListName({
        lastName: getStringAtPath(context, "profile.lastName"),
        firstName: getStringAtPath(context, "profile.firstName"),
        namePrefix: getStringAtPath(context, "profile.namePrefix"),
        academicTitle: getStringAtPath(context, "profile.academicTitle"),
    }),
    guestNationality: getGuestNationality,
    guestPrimaryEmail: getGuestPrimaryEmail,
    hotelAddress: (context) => getAddressLine(getValueAtPath(context, "organization.address")),
    hotelContactLine: (context) => compact([
        getHotelPhone(context),
        getValueAtPath(context, "organization.primaryEmail"),
    ]).join(CONTACT_SEPARATOR),
    hotelCountry: getHotelCountry,
    hotelLegalLine: getHotelLegalLine,
    printedAt: (context, meta) => formatDateTimeValue(context.now ?? new Date(), meta),
    reservationAdultCount: (context) => getGuestCountForAgeType(context, "ADULT"),
    reservationChildCount: (context) => getGuestCountForAgeType(context, "CHILD"),
    reservationFacilityNames: (context, meta) => unique(
        getReservationFacilityResources(context, meta)
            .map(resource => getRecordName(resource, meta))
            .filter((name): name is string => Boolean(name)),
    ),
    reservationGuestBreakdown: (context, meta) => {
        const counts = getGuestCounts(context);
        const ageCategories = getAgeCategories(context);

        return counts
            .map((guestCount) => {
                const count = typeof guestCount.count === "number" ? guestCount.count : 0;
                if (!count) return "";

                const ageCategory = ageCategories.find((category) => category.id === guestCount.ageCategoryId);
                const label = getAgeCategoryCountLabel(ageCategory, count, meta)
                    ?? RESERVATION_GUEST_COUNT_LABELS[meta.language]
                    ?? RESERVATION_GUEST_COUNT_LABELS.en;

                return `${formatNumber(count, meta)} ${label}`;
            })
            .filter(Boolean);
    },
    reservationDuration: getReservationDuration,
    reservationEatPeriod: getReservationEatPeriod,
    reservationMealPlanDateRange: getMealPlanDateRange,
    reservationGuestCount: getTotalGuestCount,
    reservationStatus: getReservationStatus,
    reservationInfantCount: (context) => getGuestCountForAgeType(context, "INFANT"),
    resourceCategoryNames: (context, meta) => unique(
        getResourceCategories(context)
            .map((category) => getRecordName(category, meta))
            .filter((name): name is string => Boolean(name)),
    ),
    reservationResourceNames: (context, meta) => unique(
        getReservationDisplayResources(context)
            .map((resource) => getRecordName(resource, meta))
            .filter((name): name is string => Boolean(name)),
    ),
    reservationStaffNames: (context, meta) => unique(
        getReservationStaffResources(context, meta)
            .map(resource => getRecordName(resource, meta))
            .filter((name): name is string => Boolean(name)),
    ),
    reservationRestaurantName: (context, meta) => {
        // Prevent restaurant fallback from selecting unrelated spa/accommodation resources.
        const sectorType = getSectorType(context);
        if (sectorType && sectorType !== "RESTAURANT") return "";

        const restaurantName = getRecordName(getRestaurantResource(context) ?? {}, meta);
        if (restaurantName) return restaurantName;

        const parentNames = getResourcesForReservation(context)
            .map((resource) => getResourceById(context, resource.parentId))
            .filter(isRecord)
            .map((resource) => getRecordName(resource, meta))
            .filter((name): name is string => Boolean(name));

        if (parentNames.length) return unique(parentNames);

        const primaryResourceName = context.resource ? getRecordName(context.resource, meta) : undefined;
        return primaryResourceName ?? "";
    },
    reservationRoomNames: (context, meta) => {
        const sectorType = getSectorType(context);
        if (sectorType && sectorType !== "ACCOMMODATION") return [];

        const roomNames = getReservationRoomResources(context)
            .map(room => getRecordName(room, meta))
            .filter((name): name is string => Boolean(name));

        return unique(roomNames);
    },
    reservationRoomCategoryNames: (context, meta) => {
        const sectorType = getSectorType(context);
        if (sectorType && sectorType !== "ACCOMMODATION") return [];

        return unique(
            getReservationRoomResources(context)
                .flatMap(resource => getResourceCategoriesForResource(context, resource))
                .map(category => getRecordName(category, meta))
                .filter((name): name is string => Boolean(name)),
        );
    },
    reservationTableNames: (context, meta) => {
        const sectorType = getSectorType(context);
        if (sectorType && sectorType !== "RESTAURANT") return [];

        return unique(
            getRestaurantTableResources(context)
                .map((resource) => getRecordName(resource, meta))
                .filter((name): name is string => Boolean(name)),
        );
    },
    reservationTeenagerCount: (context) => getGuestCountForAgeType(context, "TEENAGER"),
    serviceLocalizedDescription: (context, meta) => getServiceLocalizedDescription(context, meta, false),
    serviceLocalizedName: getServiceLocalizedName,
    serviceLocalizedShortDescription: (context, meta) => getServiceLocalizedDescription(context, meta, true),
    servicePrice: getServicePrice,
    servicePriceBeforeTax: getServicePriceBeforeTax,
    serviceRateDescription: getServiceRateDescription,
    serviceRateName: getServiceRateName,
    serviceCategoryNames: (context, meta) => unique(
        getServiceCategories(context)
            .map((category) => getRecordName(category, meta))
            .filter((name): name is string => Boolean(name)),
    ),
};

const resolveRawValue = (
    variable: PrintoutVariableDefinition,
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
) => {
    const rawValue = variable.attr.compute
        ? computeResolvers[variable.attr.compute](context, meta)
        : variable.attr.path
            ? getValueAtPath(context, variable.attr.path)
            : undefined;

    if (variable.attr.kind === "localizedText") {
        return getLocalizedText(rawValue, meta);
    }

    return rawValue;
};

const formatResolvedValue = (
    variable: PrintoutVariableDefinition,
    rawValue: unknown,
    context: PrintoutVariableResolveContext,
    meta: LanguageMeta,
    formatting?: PrintoutVariableFormatting,
) => {
    switch (variable.attr.kind) {
        case "currency":
            return formatCurrency(rawValue, context, variable, meta);
        case "date":
            return formatDate(
                rawValue,
                meta,
                normalizePrintoutDateFormat(formatting?.dateFormat) ?? "numeric",
            );
        case "duration":
            return typeof rawValue === "number"
                ? formatDurationMinutes(rawValue, meta)
                : formatPlainValue(rawValue, meta);
        case "time":
            return formatTime(rawValue, meta);
        case "list":
            return Array.isArray(rawValue)
                ? rawValue.map((value) => formatPlainValue(value, meta)).filter(Boolean).join(LIST_SEPARATOR)
                : formatPlainValue(rawValue, meta);
        case "boolean":
        case "localizedText":
        case "number":
        case "text":
        default:
            return formatPlainValue(rawValue, meta);
    }
};

export const resolvePrintoutVariable = (
    reference: PrintoutVariableKey | string,
    context: PrintoutVariableResolveContext,
    formatting?: PrintoutVariableFormatting,
): ResolvedPrintoutVariable | undefined => {
    const parsed = parsePrintoutVariableReference(reference);
    const variable = findPrintoutVariable(parsed.key);
    if (!variable) return undefined;
    const displayReference = getPrintoutVariableDisplayReference(reference);

    const meta = getLanguageMeta(parsed.language, context);
    const rawValue = resolveRawValue(variable, context, meta);
    const displayValue = formatResolvedValue(variable, rawValue, context, meta, formatting);

    return {
        displayValue,
        isEmpty: !displayValue,
        key: variable.key,
        language: meta.language,
        locale: meta.locale,
        rawValue,
        reference: displayReference,
        variable,
    };
};

export const resolvePrintoutVariableDisplayValue = (
    reference: PrintoutVariableKey | string,
    context: PrintoutVariableResolveContext,
    formatting?: PrintoutVariableFormatting,
) => resolvePrintoutVariable(reference, context, formatting)?.displayValue ?? "";
